import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../../components/ui/Card';
import { Button } from '../../components/ui/Button';
import { Badge } from '../../components/ui/Badge';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '../../components/ui/Table';
import { Skeleton } from '../../components/ui/Skeleton';
import {
  Activity,
  CheckCircle2,
  AlertTriangle,
  XCircle,
  RefreshCw,
  Building2,
  Bell,
  Clock,
} from 'lucide-react';
import { formatDistanceToNow } from 'date-fns';
import { ptBR } from 'date-fns/locale';
import { toast } from 'sonner';
import {
  usePlatformHealthSummary,
  useFinancialAlerts,
  useAcknowledgeAlert,
} from '../../hooks/useFinancialSecurity';
import { HealthSummaryCards } from '../../components/financial-security';
import { AlertNotification } from '../../components/financial-security/AlertBadge';

export function FinancialHealthDashboard() {
  const { data: healthSummary, isLoading: loadingHealth, refetch: refetchHealth } = usePlatformHealthSummary();
  const { data: alerts, isLoading: loadingAlerts } = useFinancialAlerts({ acknowledged: false });
  const acknowledgeAlert = useAcknowledgeAlert();

  const handleAcknowledge = async (alertId: string) => {
    try {
      await acknowledgeAlert.mutateAsync(alertId);
      toast.success('Alerta reconhecido');
    } catch (error: any) {
      toast.error(error.message || 'Erro ao reconhecer alerta');
    }
  };

  if (loadingHealth) {
    return (
      <div className="container mx-auto py-6 space-y-6">
        <Skeleton className="h-8 w-64" />
        <div className="grid gap-4 md:grid-cols-4">
          {[1, 2, 3, 4].map((i) => (
            <Skeleton key={i} className="h-32" />
          ))}
        </div>
        <Skeleton className="h-96" />
      </div>
    );
  }

  return (
    <div className="container mx-auto py-6 space-y-6">
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-3">
          <Activity className="h-8 w-8 text-primary" />
          <div>
            <h1 className="text-2xl font-bold">Saúde Financeira da Plataforma</h1>
            <p className="text-muted-foreground">
              Visão geral de todos os ativos tokenizados
            </p>
          </div>
        </div>
        <Button variant="outline" onClick={() => refetchHealth()}>
          <RefreshCw className="h-4 w-4 mr-2" />
          Atualizar
        </Button>
      </div>

      {/* Summary Cards */}
      {healthSummary && (
        <HealthSummaryCards
          totalAssets={healthSummary.totalAssets}
          healthyAssets={healthSummary.healthyAssets}
          warningAssets={healthSummary.warningAssets}
          criticalAssets={healthSummary.criticalAssets}
        />
      )}

      {/* Additional Metrics */}
      <div className="grid gap-4 md:grid-cols-2">
        <Card>
          <CardHeader className="pb-2">
            <CardTitle className="text-sm font-medium text-muted-foreground">
              Distribuições Pendentes
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="flex items-center gap-2">
              <Clock className="h-5 w-5 text-yellow-600" />
              <span className="text-2xl font-bold">
                {healthSummary?.pendingYieldDistributions ?? 0}
              </span>
            </div>
          </CardContent>
        </Card>

        <Card>
          <CardHeader className="pb-2">
            <CardTitle className="text-sm font-medium text-muted-foreground">
              Pagamentos com Falha
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="flex items-center gap-2">
              <XCircle className={`h-5 w-5 ${(healthSummary?.failedYieldPayments ?? 0) > 0 ? 'text-red-600' : 'text-gray-400'}`} />
              <span className={`text-2xl font-bold ${(healthSummary?.failedYieldPayments ?? 0) > 0 ? 'text-red-600' : ''}`}>
                {healthSummary?.failedYieldPayments ?? 0}
              </span>
            </div>
          </CardContent>
        </Card>
      </div>

      <div className="grid gap-6 lg:grid-cols-2">
        {/* Tenant Summary */}
        <Card>
          <CardHeader>
            <div className="flex items-center gap-2">
              <Building2 className="h-5 w-5" />
              <CardTitle className="text-lg">Saúde por Tenant</CardTitle>
            </div>
            <CardDescription>
              Status de saúde dos ativos por tenant
            </CardDescription>
          </CardHeader>
          <CardContent>
            {healthSummary?.tenantSummaries && healthSummary.tenantSummaries.length > 0 ? (
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>Tenant</TableHead>
                    <TableHead className="text-center">
                      <CheckCircle2 className="h-4 w-4 inline text-green-600" />
                    </TableHead>
                    <TableHead className="text-center">
                      <AlertTriangle className="h-4 w-4 inline text-yellow-600" />
                    </TableHead>
                    <TableHead className="text-center">
                      <XCircle className="h-4 w-4 inline text-red-600" />
                    </TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {healthSummary.tenantSummaries.map((tenant) => (
                    <TableRow key={tenant.tenantId}>
                      <TableCell className="font-medium">{tenant.tenantName}</TableCell>
                      <TableCell className="text-center text-green-600">{tenant.healthy}</TableCell>
                      <TableCell className="text-center text-yellow-600">{tenant.warning}</TableCell>
                      <TableCell className="text-center text-red-600">{tenant.critical}</TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            ) : (
              <div className="text-center py-8 text-muted-foreground">
                <Building2 className="h-12 w-12 mx-auto mb-2 opacity-50" />
                <p>Nenhum ativo tokenizado na plataforma</p>
              </div>
            )}
          </CardContent>
        </Card>

        {/* Active Alerts */}
        <Card>
          <CardHeader>
            <div className="flex items-center gap-2">
              <Bell className="h-5 w-5" />
              <CardTitle className="text-lg">Alertas Ativos</CardTitle>
              {alerts && alerts.length > 0 && (
                <Badge variant="destructive">{alerts.length}</Badge>
              )}
            </div>
            <CardDescription>
              Alertas de segurança não reconhecidos
            </CardDescription>
          </CardHeader>
          <CardContent>
            {loadingAlerts ? (
              <Skeleton className="h-32" />
            ) : alerts && alerts.length > 0 ? (
              <div className="space-y-3 max-h-96 overflow-y-auto">
                {alerts.slice(0, 10).map((alert) => (
                  <AlertNotification
                    key={alert.id}
                    title={alert.title}
                    message={alert.message}
                    severity={alert.severity}
                    time={formatDistanceToNow(new Date(alert.createdAt), {
                      addSuffix: true,
                      locale: ptBR,
                    })}
                    onAcknowledge={() => handleAcknowledge(alert.id)}
                  />
                ))}
                {alerts.length > 10 && (
                  <p className="text-center text-sm text-muted-foreground">
                    +{alerts.length - 10} alertas adicionais
                  </p>
                )}
              </div>
            ) : (
              <div className="text-center py-8 text-muted-foreground">
                <CheckCircle2 className="h-12 w-12 mx-auto mb-2 text-green-500 opacity-50" />
                <p>Nenhum alerta ativo</p>
                <p className="text-sm">Todos os sistemas operando normalmente</p>
              </div>
            )}
          </CardContent>
        </Card>
      </div>

      {/* Last Updated */}
      {healthSummary?.lastUpdated && (
        <p className="text-xs text-muted-foreground text-center">
          Última atualização: {formatDistanceToNow(new Date(healthSummary.lastUpdated), {
            addSuffix: true,
            locale: ptBR,
          })}
        </p>
      )}
    </div>
  );
}

export default FinancialHealthDashboard;
